/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { LinearBillingInterface } from '../../../interfaces/billing/LinearBillingInterface';
import Loader from '../../../components/Loaders/Loader';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import PrefixInterface from '../../../interfaces/billing/PrefixInterface';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Icon, Tooltip } from '@material-ui/core';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import AddButton from '../../../components/Buttons/AddButton';
import Pager from '../../../components/Adescom/Pager';
import RemoveButton from '../../../components/Buttons/RemoveButton';
import BasePricelistInterface from '../../../interfaces/billing/BasePricelistInterface';
import { PrefixCollectionInterface } from '../../../interfaces/api/PrefixCollectionInterface';
import Close from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import WarningIcon from '@material-ui/icons/Warning';
import { NotificationHelper } from '../../../helpers/NotificationHelper';

const useStyle = makeStyles(adescomStyle);

interface PrefixesTabProps {
    pricelist: BasePricelistInterface | null;
    linearBilling: LinearBillingInterface;
    prefixes: PrefixCollectionInterface | undefined;
    selectedPrefixes: Array<PrefixInterface>;
    updatePrefixesList: (prefixes: PrefixCollectionInterface) => void;
    updateSelectedPrefixesList: (prefixes: Array<PrefixInterface>) => void;
}

export const PrefixesTab = (props: PrefixesTabProps): React.ReactElement => {
    const dispatch = useDispatch();
    const classes = useStyle();

    const pricelist = props.pricelist;
    const linearBilling = props.linearBilling;
    const prefixes = props.prefixes;
    const selectedPrefixes = props.selectedPrefixes;
    const updatePrefixesList = props.updatePrefixesList;
    const updateSelectedPrefixesList = props.updateSelectedPrefixesList;

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const [prefixesLoaded, setPrefixesLoaded] = useState<boolean>(false);
    const [selectedPrefixesLoaded, setSelectedPrefixesLoaded] = useState<boolean>(false);

    const [prefixFilters, setPrefixFilters] = useState<any>([]);
    const [prefixOrders, setPrefixOrders] = useState<any>([]);
    const loadPrefixes = useCallback(
        (uri?: string): void => {
            setPrefixesLoaded(false);

            let tmpUri = FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/prefixes`, configDefault.PLATFORM_API_PREFIX));
            if (uri) {
                tmpUri = FormGeneralHelper.getByUri(uri);
            }

            const tmpPrefixFilters = [...prefixFilters, { name: 'type', value: linearBilling.type }];

            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                tmpUri,
                'PREFIXES_GET',
                tmpPrefixFilters,
                prefixOrders,
                (data: PrefixCollectionInterface) => {
                    updatePrefixesList(data);
                    setPrefixesLoaded(true);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch, prefixFilters, prefixOrders, linearBilling.type, updatePrefixesList]
    );

    const loadSelectedPrefixes = useCallback((): void => {
        if (!pricelist) {
            setSelectedPrefixesLoaded(true);
            return;
        }
        let tmpUri = FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/custom_billing_action`, configDefault.PLATFORM_API_PREFIX));

        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'get_pricelist_selected_prefixes',
            {
                action: 'get_pricelist_selected_prefixes',
                params: JSON.stringify({
                    selectedPrefixes: pricelist.prefixes,
                }),
            },
            {
                onSuccess: (data: PrefixCollectionInterface) => {
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        updateSelectedPrefixesList(data['hydra:member']);
                    }
                    setSelectedPrefixesLoaded(true);
                },
                onError: () => {
                    setSelectedPrefixesLoaded(true);
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [dispatch, pricelist, updateSelectedPrefixesList]);

    const updatePrefixFilters = useCallback(
        (filters: any): void => {
            setPrefixFilters(filters);
            loadPrefixes();
        },
        [loadPrefixes]
    );

    const updatePrefixOrders = useCallback(
        (orders: any): void => {
            setPrefixOrders(orders);
            loadPrefixes();
        },
        [loadPrefixes]
    );

    const addPrefixes = useCallback(
        (param: { el: PrefixInterface }): void => {
            if (
                !selectedPrefixes.some((pf: PrefixInterface) => {
                    return pf.id === param.el.id;
                })
            ) {
                let newArr = [...selectedPrefixes, param.el];
                updateSelectedPrefixesList(newArr);
            }
        },
        [selectedPrefixes, updateSelectedPrefixesList]
    );
    const removePrefixes = useCallback(
        (param: { el: PrefixInterface }): void => {
            let newArr = [...selectedPrefixes];
            selectedPrefixes.forEach((el: PrefixInterface, indx: number) => {
                if (el.id === param.el.id) {
                    newArr.splice(indx, 1);
                }
            });
            updateSelectedPrefixesList(newArr);
        },
        [selectedPrefixes, updateSelectedPrefixesList]
    );

    useEffect(() => {
        if (!prefixes) {
            loadPrefixes();
            loadSelectedPrefixes();
        } else {
            setPrefixesLoaded(true);
            setSelectedPrefixesLoaded(true);
        }
    }, [loadPrefixes, loadSelectedPrefixes, prefixes]);

    return (
        <>
            <Loader visible={!prefixesLoaded || !selectedPrefixesLoaded} />
            <h4>
                <I18n trans={'discount:s.prefixes'} />
            </h4>
            <br />
            <GridContainer>
                <GridItem xs={6} style={{ overflowX: 'scroll' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell textAlign="left" title="clients:c.commands" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="prefixes:h.prefix_name"
                                        updateOrderList={updatePrefixOrders}
                                        updateFilterList={updatePrefixFilters}
                                        fieldName="mainname"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        searchActionCallback={loadPrefixes}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="prefixes:h.prefix"
                                        updateOrderList={updatePrefixOrders}
                                        updateFilterList={updatePrefixFilters}
                                        fieldName="prefix"
                                        showSort={true}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                        searchActionCallback={loadPrefixes}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {prefixes &&
                                prefixes['hydra:member'] &&
                                prefixes['hydra:member'].map((el: PrefixInterface) => {
                                    return (
                                        <TableRow key={el.id}>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_PUT) && (
                                                    <AddButton
                                                        tooltipTitle="pricelists:button.assign_prefix"
                                                        callback={addPrefixes}
                                                        callbackParam={{ el: el }}
                                                        disabled={selectedPrefixes.some((sf: PrefixInterface) => {
                                                            return sf.id === el.id;
                                                        })}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {el.mainname}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {el.prefix}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell classes={tableCellRoot} colSpan={4}>
                                    {prefixes && prefixes['hydra:view'] && <Pager callback={loadPrefixes} views={prefixes['hydra:view']} />}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </GridItem>
                <GridItem xs={6} style={{ overflowX: 'scroll' }}>
                    <div style={{ maxHeight: 700, overflowY: 'auto' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell textAlign="left" title="clients:c.commands" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell textAlign="left" title="prefixes:h.prefix_name" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell textAlign="left" title="prefixes:h.prefix" />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPrefixes &&
                                selectedPrefixes.map((el: PrefixInterface) => {
                                    return (
                                        <TableRow key={el.id}>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {linearBilling.type !== el.type && (
                                                    <Tooltip title={<I18n trans={'pricelists:lb.incorrect_prefix_type'} />}>
                                                        <IconButton
                                                            style={{ color: 'red' }}
                                                            className={classes.iconButton}
                                                            key="close"
                                                            aria-label="Close"
                                                            onClick={(): void => {
                                                                removePrefixes({ el: el });
                                                            }}
                                                        >
                                                            <WarningIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_PUT) && (
                                                    <RemoveButton
                                                        tooltipTitle="pricelists:button.remove_assigned_prefix"
                                                        callback={removePrefixes}
                                                        callbackParam={{ el: el }}
                                                        disabled={
                                                            !selectedPrefixes.some((sf: PrefixInterface) => {
                                                                return sf.id === el.id;
                                                            })
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {el.mainname}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} align="left">
                                                {el.prefix}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </div>
                </GridItem>
            </GridContainer>
        </>
    );
};
